// src/components/ui/Button/index.tsx
import { ColorKeys, createColorClasses } from "@/theme/colors";
import clsx from "clsx";
import "./Button.scss";
import { Download, Expand, Info, Spinner } from "@/icons";
import * as React from "react";
import { CommonIconsType } from "@/types/icons.ts";
import { Col } from "@/components";

type ButtonSize = "2" | "3" | "4" | "5";
type ButtonVariant = "primary" | "secondary" | "closeModal";

type BaseButtonProps = {
  children: React.ReactNode;
  icon?: CommonIconsType;
  variant?: ButtonVariant;
  variantBg?: string;
  bg?: ColorKeys;
  textColor?: ColorKeys;
  border?: ColorKeys;
  size?: ButtonSize;
  className?: string;
  disabled?: boolean;
  loading?: boolean;
  form?: string;
  rel?: string;
  target?: string;
  isExternal?: boolean;
  fwClassName?: "fw-bold" | "fw-semibold" | "fw-normal";
};

type ButtonAsButtonProps = BaseButtonProps & {
  onClick?: (e?: React.MouseEvent<HTMLButtonElement>) => void;
  type?: "button" | "submit" | "reset";
  asLink?: never;
  href?: never;
};

type ButtonAsLinkProps = BaseButtonProps & {
  href: string;
  onClick?: never;
  type?: never;
  asLink?: "a" | React.ComponentType<any>;
};

type ButtonProps = ButtonAsButtonProps | ButtonAsLinkProps;

export const Button = ({
  children,
  variant,
  variantBg = "bg-white",
  bg = "primary",
  textColor = "white",
  border = "white",
  size = "3",
  className,
  disabled,
  loading,
  onClick,
  type = "button",
  form,
  asLink,
  href,
  isExternal = false,
  target = undefined,
  rel = undefined,
  icon = undefined,
  fwClassName = "fw-bold",
  ...props
}: ButtonProps) => {
  // Only apply color classes if no variant is specified
  const colorClasses = !variant
    ? createColorClasses({
        bg,
        text: textColor,
        border,
      })
    : "";

  const BTN_SIZES = {
    "2": "fs-6 py-x12 px-x24",
    "3": "fs-7 py-x12 px-x24",
    "4": "fs-6 py-x4 px-x8",
    "5": "fs-7 md-fs-6 md-px-x16 py-x12",
  };

  const baseClasses = clsx(
    "ui-button",
    BTN_SIZES[size],
    // Apply variant class if provided
    variant && `ui-button-${variant} ${variantBg}`,
    // Otherwise use traditional classes
    !variant && colorClasses,
    {
      "ui-button-disabled": disabled || loading,
    },
    {
      "ui-button-loading": loading,
    },
    className,
    fwClassName,
  );

  if (asLink) {
    const LinkComponent = asLink;
    return (
      <LinkComponent
        target={isExternal ? "_blank" : target}
        rel={isExternal ? "noopener noreferrer" : rel}
        href={href}
        className={baseClasses}
        {...props}
      >
        {children}
      </LinkComponent>
    );
  }

  const ButtonIcon = () => {
    switch (icon) {
      case "info":
        return <Info height={16} />;
      case "download":
        return <Download height={16} />;
      case "expand":
        return <Expand width={18} height={18} />;
      default:
        return null;
    }
  };

  return (
    <button
      type={type}
      form={form}
      className={baseClasses}
      onClick={onClick}
      disabled={disabled || loading}
      {...props}
    >
      {icon && (
        <Col mr={"x8"}>
          {!loading && <ButtonIcon />}
          {loading ? <Spinner height={16} /> : null}
        </Col>
      )}

      {children}

      {loading && !icon ? <Spinner height={16} /> : null}
    </button>
  );
};
